import { defineStore } from 'pinia'
import api from '../api/index.js'

// Prevent race conditions during initialization
let initializationPromise = null

export const useAuthStore = defineStore('auth', {
    state: () => ({
        isAuthenticated: false,
        isAccessForbidden: false,   // 인증은 됐지만 정책(예: AdministrationPolicy) 위반 — 403
        isLoading: true,
        isInitialized: false,
        user: null,
        antiforgeryToken: null,
        error: null,
    }),

    getters: {
        userId: (state) => state.user?.id,
        userName: (state) => state.user?.userName || state.user?.name,
        userEmail: (state) => state.user?.email,
        userRoles: (state) => state.user?.roles || [],
        isAdmin: (state) => {
            const roles = state.user?.roles || []
            return roles.includes('IdentityAdminAdministrator') ||
                   roles.includes('ChurchAdminManager') ||
                   roles.includes('Administrator')
        },
    },

    actions: {
        /**
         * Initialize auth state on app load
         * Fetches current user info and antiforgery token from server
         */
        async initialize() {
            if (this.isInitialized) {
                console.log('[AuthStore] Already initialized')
                return
            }

            // Prevent duplicate initialization
            if (initializationPromise) {
                console.log('[AuthStore] Waiting for existing initialization...')
                return initializationPromise
            }

            initializationPromise = (async () => {
                try {
                    this.isLoading = true
                    console.log('[AuthStore] Initializing...')

                    // Fetch current user info and antiforgery token
                    // This endpoint is provided by Admin backend
                    let forbiddenSeen = false
                    const response = await api.get('/api/auth/me').catch((e) => {
                        console.warn('[AuthStore] Auth check failed:', e.status)
                        if (e?.status === 403) forbiddenSeen = true
                        return { data: null }
                    })

                    if (response.data) {
                        this.user = response.data.user
                        this.antiforgeryToken = response.data.antiforgeryToken
                        this.isAuthenticated = true
                        this.isAccessForbidden = false
                        console.log('[AuthStore] User authenticated:', this.userName)
                    } else {
                        this.isAuthenticated = false
                        this.isAccessForbidden = forbiddenSeen   // 403: 인증은 됐지만 권한 부족
                        this.user = null
                    }

                    console.log('[AuthStore] Initialization complete')
                } catch (error) {
                    console.error('[AuthStore] Initialization failed:', error)
                    this.error = error
                    this.isAuthenticated = false
                } finally {
                    this.isLoading = false
                    this.isInitialized = true
                    initializationPromise = null
                }
            })()

            return initializationPromise
        },

        /**
         * Clear authentication state
         */
        clearAuth() {
            this.isAuthenticated = false
            this.isAccessForbidden = false
            this.user = null
            this.antiforgeryToken = null
        },

        /**
         * Refresh user info
         */
        async refreshUser() {
            try {
                const response = await api.get('/api/auth/me')
                if (response.data) {
                    this.user = response.data.user
                    this.antiforgeryToken = response.data.antiforgeryToken
                    this.isAuthenticated = true
                }
            } catch (error) {
                console.error('[AuthStore] User refresh failed:', error)
                this.isAuthenticated = false
                this.user = null
            }
        },

        /**
         * Logout — client-local (세션 정책 v3 Phase E, 2026-07-29).
         * 자기 cookie 세션 + refresh_token revoke 만. STS end-session 은 호출하지 않으므로
         * 다른 패밀리 사이트 로그인은 유지된다.
         * 서버 라우트는 POST + antiforgery 필수 (로그아웃 CSRF 방지) — form POST 로 제출.
         * (기존 GET '/Account/Logout' 은 Area 라우트 불일치로 index.html fallback 에 빠지는
         *  깨진 상태였음 — 올바른 경로는 '/AdminUI/Account/...')
         */
        async logout() {
            // 토큰이 없으면 먼저 재발급 시도 — 빈 토큰 POST 는 400 이 나고
            // clearAuth 만 된 반쪽 상태(서버 세션 잔존)가 됨 (Codex 리뷰)
            let token = this.antiforgeryToken
            if (!token) {
                try {
                    const res = await api.post('/api/auth/refresh-token')
                    token = res.data?.antiforgeryToken
                } catch (e) {
                    console.error('[AuthStore] antiforgery 토큰 재발급 실패 — 로그아웃 중단', e)
                    return
                }
            }
            this.clearAuth()
            const form = document.createElement('form')
            form.method = 'post'
            form.action = '/AdminUI/Account/LogoutLocal'
            const input = document.createElement('input')
            input.type = 'hidden'
            input.name = '__RequestVerificationToken'
            input.value = token
            form.appendChild(input)
            document.body.appendChild(form)
            form.submit()
        },

        /**
         * Reset store
         */
        reset() {
            this.$reset()
        },
    },
})
